import React, { useState, useEffect } from 'react';
import { RatingSummaryProps, RatingSummary } from '../../types/review-types';

export const RatingSummaryWidget: React.FC<RatingSummaryProps> = ({
  entityId,
  entityType,
  showBreakdown = false,
  compact = false,
  loading = false,
  error
}) => {
  const [ratingSummary, setRatingSummary] = useState<RatingSummary | null>(null);
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);

  // Mock data for now - in real implementation, this would fetch from API
  useEffect(() => {
    if (!loading && !error) {
      // Simulate API call
      const mockSummary: RatingSummary = {
        averageRating: 4.3,
        totalReviews: 127,
        ratingDistribution: {
          5: 65,
          4: 42,
          3: 15,
          2: 3,
          1: 2
        },
        categoryAverages: entityType === 'listing' ? {
          cleanliness: 4.5,
          accuracy: 4.2,
          communication: 4.4,
          location: 4.1,
          value: 4.0
        } : entityType === 'guest' ? {
          guestCommunication: 4.3,
          guestCleanliness: 4.1,
          houseRulesAdherence: 4.5
        } : {
          communication: 4.4,
          responseTime: 4.2,
          hospitality: 4.6
        }
      };
      setRatingSummary(mockSummary);
    }
  }, [entityId, entityType, loading, error]);

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'w-3 h-3',
      md: 'w-4 h-4',
      lg: 'w-5 h-5'
    };
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`${sizeClasses[size]} ${
              star <= Math.floor(rating) 
                ? 'text-yellow-400 fill-current' 
                : star <= rating 
                ? 'text-yellow-400 fill-current opacity-50'
                : 'text-gray-300'
            }`}
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  const renderRatingDistribution = (distribution: Record<number, number>, total: number) => {
    return (
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = distribution[rating] || 0;
          const percentage = total > 0 ? (count / total) * 100 : 0;
          
          return (
            <div key={rating} className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-700 w-8">
                {rating}★
              </span>
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-sm text-gray-600 w-8 text-right">
                {count}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCategoryBreakdown = (categoryAverages: Record<string, number>) => {
    const categoryLabels: Record<string, string> = {
      cleanliness: 'Cleanliness',
      accuracy: 'Accuracy',
      communication: 'Communication',
      location: 'Location',
      value: 'Value',
      guestCommunication: 'Communication',
      guestCleanliness: 'Cleanliness',
      houseRulesAdherence: 'House Rules',
      responseTime: 'Response Time',
      hospitality: 'Hospitality'
    };

    return (
      <div className="space-y-3">
        {Object.entries(categoryAverages).map(([category, rating]) => (
          <div key={category} className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {categoryLabels[category] || category}
            </span>
            <div className="flex items-center space-x-2">
              {renderStars(rating, 'sm')}
              <span className="text-sm text-gray-600 w-8 text-right">
                {rating.toFixed(1)}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${compact ? 'p-4' : 'p-6'} animate-pulse`}>
        <div className="space-y-3">
          <div className="h-4 bg-gray-300 rounded w-1/3"></div>
          <div className="flex items-center space-x-2">
            <div className="h-5 bg-gray-300 rounded w-20"></div>
            <div className="h-4 bg-gray-300 rounded w-16"></div>
          </div>
          {!compact && (
            <div className="space-y-2">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="h-3 bg-gray-300 rounded"></div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg ${compact ? 'p-4' : 'p-6'}`}>
        <div className="flex items-center space-x-2">
          <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-red-800 text-sm font-medium">Error loading ratings</span>
        </div>
      </div>
    );
  }

  if (!ratingSummary || ratingSummary.totalReviews === 0) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg ${compact ? 'p-4' : 'p-6'} text-center`}>
        <div className="text-gray-500">
          <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
          <p className="text-sm font-medium">
            {entityType === 'listing' ? 'New listing' : entityType === 'guest' ? 'New guest' : 'New host'}
          </p>
          <p className="text-xs mt-1">No reviews yet</p>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center space-x-3">
        {renderStars(ratingSummary.averageRating, 'sm')}
        <span className="text-sm font-medium text-gray-900">
          {ratingSummary.averageRating.toFixed(1)}
        </span>
        <span className="text-sm text-gray-500">
          ({ratingSummary.totalReviews} review{ratingSummary.totalReviews !== 1 ? 's' : ''})
        </span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          {entityType === 'listing' ? 'Guest Reviews' : entityType === 'guest' ? 'Host Reviews' : 'Reviews'}
        </h3>
        {showBreakdown && Object.keys(ratingSummary.categoryAverages).length > 0 && (
          <button
            onClick={() => setShowDetailedBreakdown(!showDetailedBreakdown)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
          >
            {showDetailedBreakdown ? 'Hide details' : 'Show details'}
          </button>
        )}
      </div>

      {/* Overall Rating */}
      <div className="flex items-center space-x-4 mb-6">
        <div className="text-center">
          <div className="text-3xl font-bold text-gray-900 mb-1">
            {ratingSummary.averageRating.toFixed(1)}
          </div>
          {renderStars(ratingSummary.averageRating, 'lg')}
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-2">
            Based on {ratingSummary.totalReviews} review{ratingSummary.totalReviews !== 1 ? 's' : ''}
          </p>
          {renderRatingDistribution(ratingSummary.ratingDistribution, ratingSummary.totalReviews)}
        </div>
      </div>

      {/* Category Breakdown */}
      {showBreakdown && showDetailedBreakdown && Object.keys(ratingSummary.categoryAverages).length > 0 && (
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Rating Breakdown</h4>
          {renderCategoryBreakdown(ratingSummary.categoryAverages)}
        </div>
      )}

      {/* Quick Category Overview (always shown if categories exist) */}
      {!showDetailedBreakdown && Object.keys(ratingSummary.categoryAverages).length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(ratingSummary.categoryAverages).slice(0, 4).map(([category, rating]) => {
              const categoryLabels: Record<string, string> = {
                cleanliness: 'Cleanliness',
                accuracy: 'Accuracy',
                communication: 'Communication',
                location: 'Location',
                value: 'Value',
                guestCommunication: 'Communication',
                guestCleanliness: 'Cleanliness',
                houseRulesAdherence: 'House Rules'
              };
              
              return (
                <div key={category} className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">
                    {categoryLabels[category] || category}
                  </span>
                  <div className="flex items-center space-x-1">
                    {renderStars(rating, 'sm')}
                    <span className="text-xs text-gray-500">
                      {rating.toFixed(1)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RatingSummaryWidget;